import { collection, getDocs, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "./config";

const functions = getFunctions(undefined, "europe-central2");
const manageRanking = httpsCallable<Record<string, unknown>, any>(functions, "manageRankingLifecycle");

export type RankingEditionStatus = "DRAFT" | "CONFIRMED" | "FINAL";

export interface RankingEdition {
  id: string;
  title: string;
  year: number;
  ageGroup?: string;
  gender?: string;
  weightClass?: string;
  status: RankingEditionStatus;
  revision: number;
  contributionCount: number;
  totalPoints: number;
  updatedAt?: any;
}

export interface RankingContribution {
  id: string;
  editionId: string;
  athleteId: string;
  athleteName: string;
  sourceType: "RESULT" | "MANUAL" | "ADJUSTMENT";
  sourceLabel: string;
  competitionName?: string;
  placement?: number | null;
  clubName?: string;
  points: number;
  rankingEligible: boolean;
  reason: string;
  createdAt?: any;
}

export interface FinalDrawSummary {
  id: string;
  competitionId: string;
  competitionName: string;
  category: string;
  division: string;
  weightCategory: string;
  activeVersionId: string;
  status: "FINAL";
}

export interface FinalDrawRankingPreview {
  drawId: string;
  versionId: string;
  competitionId: string;
  competitionName: string;
  category: string;
  division: string;
  weightCategory: string;
  resultFingerprint: string;
  placements: Array<{
    athleteId: string;
    athleteName: string;
    clubName: string;
    placement: 1 | 2 | 3;
    rankingEligible: boolean;
  }>;
}

export async function getRankingEditions(): Promise<RankingEdition[]> {
  const snap = await getDocs(collection(db, "rankingEditions"));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as RankingEdition))
    .sort((a, b) => b.year - a.year || a.title.localeCompare(b.title));
}

export async function getRankingContributions(editionId: string): Promise<RankingContribution[]> {
  const snap = await getDocs(query(collection(db, "rankingContributions"), where("editionId", "==", editionId)));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as RankingContribution));
}

export async function getFinalTournamentDraws(): Promise<FinalDrawSummary[]> {
  const snap = await getDocs(query(collection(db, "tournamentDraws"), where("status", "==", "FINAL")));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as FinalDrawSummary))
    .sort((a, b) => a.competitionName.localeCompare(b.competitionName) || a.category.localeCompare(b.category));
}

export async function previewFinalDrawRanking(drawId: string): Promise<FinalDrawRankingPreview> {
  const result = await manageRanking({ action: "preview_final_draw", drawId });
  return result.data as FinalDrawRankingPreview;
}

export async function applyFinalDrawToRanking(data: {
  editionId: string;
  drawId: string;
  resultFingerprint: string;
  points: { "1": number; "2": number; "3": number };
  reason: string;
}): Promise<{ contributionCount: number; totalPoints: number }> {
  const result = await manageRanking({ action: "apply_final_draw", ...data });
  return { contributionCount: Number(result.data.contributionCount), totalPoints: Number(result.data.totalPoints) };
}

export async function createRankingEdition(data: Pick<RankingEdition, "title" | "year"> & Partial<Pick<RankingEdition, "ageGroup" | "gender" | "weightClass">>): Promise<string> {
  const result = await manageRanking({ action: "create_edition", ...data });
  return String(result.data.editionId);
}

export async function addRankingContribution(data: {
  editionId: string;
  athleteId: string;
  athleteName: string;
  sourceType: RankingContribution["sourceType"];
  sourceLabel: string;
  points: number;
  reason: string;
  rankingEligible: boolean;
  publicExplanation?: string;
  competitionId?: string;
  competitionName?: string;
  placement?: number | null;
  clubName?: string;
}): Promise<string> {
  const result = await manageRanking({ action: "add_contribution", ...data });
  return String(result.data.contributionId);
}

export async function correctRankingContribution(data: {
  editionId: string; contributionId: string; points?: number; sourceLabel?: string; reason: string; remove?: boolean;
}): Promise<void> {
  await manageRanking({ action: "correct_contribution", ...data });
}

export async function transitionRankingEdition(edition: RankingEdition, targetStatus: RankingEditionStatus, reason = ""): Promise<void> {
  await manageRanking({ action: "transition_edition", editionId: edition.id, targetStatus, reason, expectedRevision: edition.revision });
}

export async function getPublicRankingContributions(athleteId: string): Promise<Array<Omit<RankingContribution, "reason"> & { editionTitle: string; year: number; explanation?: string }>> {
  const snap = await getDocs(query(collection(db, "publicRankingContributions"), where("athleteId", "==", athleteId)));
  return snap.docs.map((row) => ({ id: row.id, ...row.data() } as any)).sort((a, b) => b.year - a.year);
}
